import { computed, observable, action, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { validationContext, required } from 'validx'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { toStream } from '@/utils/mobx-util'
import { whileActive } from '@/utils/rx-util'
import { tap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AlertFactory } from '@/features/shell'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type Member } from '@/features/iam/members/Member'
import type { TransactionType } from '@/features/payouts/transaction-type'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import { type PayoutReviewStore } from '@/features/payouts/admin/PayoutReviewStore'
import { ConfirmUserTransactionDialogViewModel } from './ConfirmUserTransactionDialogViewModel'

/**
 * View model for the dialog that creates a new user transaction.
 */
export class NewUserTransactionDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _amountRequiringConfirmation = 500 as const
  private readonly _maxMemoLength = 280 as const
  private readonly _maxAmount = 999_999_999 as const

  readonly modal = new ModalViewModel({
    onClose: () => this.confirmationVm.modal.close(),
  })

  /**
   * The selected workspace.
   */
  @observable accessor selectedWorkspace: Workspace | null = null

  /**
   * The selected member.
   */
  @observable accessor selectedMember: Member | null = null

  @observable private accessor _memo = ''

  @observable private accessor _amount = 0

  /**
   * The transaction type.
   */
  @observable accessor transactionType: TransactionType = 'Payment'

  /**
   * The transaction reason.
   */
  @observable accessor reason: UserTransactionReason | null = null

  /**
   * Used to request confirmation for large transaction amounts.
   */
  readonly confirmationVm = new ConfirmUserTransactionDialogViewModel()

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    selectedMember: [required('Recipient is required')],
    selectedWorkspace: [required('Workspace is required')],
    memo: [
      mustHaveMaxLength(
        this._maxMemoLength,
        `The memo can be at most ${this._maxMemoLength}`,
      ),
    ],
    reason: [required('Reason is required')],
    amount: [
      ({ value }) => {
        if (value <= 0) {
          return 'Please enter a positive amount greater than zero'
        }

        if (value > this._maxAmount) {
          return `Woah there big money baller! Try a smaller amount`
        }

        return true
      },
    ],
  })

  constructor(
    private readonly _payoutReviewStore: PayoutReviewStore,
    private readonly _newAlert: AlertFactory,
  ) {
    this.searchWorkspaces = this.searchWorkspaces.bind(this)

    // Deselect the recipient when the workspace selection changes.
    this._lifecycle.manage(
      toStream(() => this.selectedWorkspace, false).pipe(
        whileActive(this.modal.showing$),
        tap(() => this.selectMember(null)),
      ),
    )

    // Deselect the reason when the type changes
    this._lifecycle.manage(
      toStream(() => this.transactionType, false).pipe(
        whileActive(this.modal.showing$),
        tap(() => this.selectReason(null)),
      ),
    )
  }

  /**
   * The transaction memo. Optional.
   */
  @computed
  get memo() {
    return this._memo
  }

  set memo(value: string) {
    this._memo = value
    this.validation.clearErrors('memo')
  }

  /**
   * The transaction amount.
   */
  @computed
  get amount() {
    return this._amount
  }

  set amount(value: number) {
    this._amount = value
    this.validation.clearErrors('amount')
  }

  /**
   * Whether the editor is disabled.
   */
  @computed
  get disabled() {
    return this.createTransaction.pending
  }

  /**
   * The possible reasons to choose from.
   */
  @computed.struct
  get availableReasons(): UserTransactionReason[] {
    switch (this.transactionType) {
      case 'Payment':
        return ['Bonus', 'MiscPayment']
      case 'Abatement':
        return ['BackgroundCheck', 'Penalty', 'MiscAbatement']
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog with empty inputs.
   */
  @action.bound
  show() {
    this.createTransaction.reset()
    this.reset()
    this.modal.show()
  }

  /**
   * Reset the inputs.
   */
  @action.bound
  reset() {
    this.selectedWorkspace = null
    this.selectedMember = null
    this.amount = 0
    this.transactionType = 'Payment'
    this.reason = null
    this.memo = ''
    this.validation.reset()
  }

  /**
   * Selects the workspace.
   */
  @action.bound
  selectWorkspace(workspace: Workspace | null | undefined) {
    this.selectedWorkspace = workspace ?? null
    this.validation.clearErrors('selectedWorkspace')
  }

  /**
   * Selects the member.
   */
  @action.bound
  selectMember(member: Member | null | undefined) {
    this.selectedMember = member ?? null
    this.validation.clearErrors('selectedMember')
  }

  /**
   * Selects the payment transaction type.
   */
  @action.bound
  selectPaymentType() {
    this.transactionType = 'Payment'
  }

  /**
   * Selects the abatement transaction type.
   */
  @action.bound
  selectAbatementType() {
    this.transactionType = 'Abatement'
  }

  /**
   * Selects the transaction reason.
   */
  @action.bound
  selectReason(reason: UserTransactionReason | null | undefined) {
    this.reason = reason ?? null
    this.validation.clearErrors('reason')
  }

  /**
   * Searches for workspaces.
   */
  searchWorkspaces(
    searchText: string,
    pagination: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ) {
    return this._payoutReviewStore.listPayoutWorkspaces(
      { ...pagination, searchText },
      signal,
    )
  }

  /**
   * Searches for members of the selected workspace. A new function for every
   * selected workspace, so that the recipient selector refetches.
   */
  @computed
  get searchMembers() {
    const workspace = this.selectedWorkspace
    return async (
      searchText: string,
      pagination: Partial<CursorPageQuery>,
      signal?: AbortSignal,
    ) => {
      if (!workspace) {
        return CursorPaged.empty<Member>()
      }

      return this._payoutReviewStore.listWorkspaceMembers(
        { ...pagination, searchText, workspaceId: workspace.id },
        signal,
      )
    }
  }

  /**
   * Creates the transaction.
   */
  createTransaction = task.resolved(async () => {
    this.validation.reset().validate()

    if (!this.validation.isValid) {
      return
    }

    if (!this.selectedMember || !this.reason) {
      return
    }

    if (this.amount >= this._amountRequiringConfirmation) {
      const confirmed = await this.confirmationVm.prompt({
        amount: this.amount,
        recipient: this.selectedMember.user,
        reason: this.reason,
      })
      if (!confirmed) {
        return
      }
    }

    await this._payoutReviewStore.createUserTransaction({
      type: this.transactionType,
      reason: this.reason,
      memo: this.memo,
      amount: this.amount,
      recipient: this.selectedMember,
    })
    this._newAlert()
      .withState('success')
      .saying('Success!', 'The transaction has been entered into the system.')
      .autoDismiss()
      .show()
    runInAction(() => {
      this.modal.close()
    })
  })
}
